import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MenuOption, Table, TableColumn, TableMenu } from "../../components/Table";
import InventoryHeader from "./InventoryHeader";
import InventoryStats from "./InventoryStats";
import CategoryFilter from "./CategoryFilter";
import AddItemModal from "../../Modal/orders/AddItemModal";
import { initAllStores } from "../../utils/cache";
import type Food from "../../models/Food";
import { CategoryType } from "../../types/category";
import { MappedFoodDrink } from "../../types/service";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { capitalizeFirst } from "../../utils/string";
import FullPageSpinner from "../../components/FullPageSpinner";
import { errorMessage, sendRequest } from "../../utils/api";
import { isEmptyObject, isOnline } from "../../utils/helper-function";
import Drink from "../../models/Drink";
import { v4 as uuidv4 } from "uuid";
import { PAGINATION_PAGE_NUMBER, } from "../../constants";
import { terminal } from "../../utils/user-helper";
import { syncServices } from "../../utils/sync";
import { formatDateTime } from "../../utils/date";

const title = {
  foods: "Food",
  drinks: "Drink"
}

const initFormData = {
  id: "",
  updatedAt: "",
  name: "",
  category: "",
  newCategory: "",
  price: "",
  openBar: "",
  gameHouse: "",
  vipLounge: "",
  privateLounge: "",
  clubHouse: "",
  description: "",
  stock: "",
  status: undefined,
}

const LOW_STOCK = 10;

const FoodDrinkInventory = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { type } = useParams<{ type: "foods" | "drinks" }>();
  const categoryKey = type === "foods" ? "foodCategory" : "drinkCategory";

  const [terminalId] = useState(() => terminal());

  const columns: any = [
    { key: "name", label: "Name" },
      { key: "updatedAt", label: "Last Update", responsive: "md" },
    { key: "category", label: "Category", responsive: "md" },
    { key: "price", label: terminalId === "t1" ? "Price" : "Standard Price" },

    ...(terminalId === "t2"
      ? [
        { key: "openBar", label: "Open Bar", responsive: "md" },
        { key: "gameHouse", label: "Game House", responsive: "md" },
        { key: "privateLounge", label: "Private Lounge", responsive: "md" },
        { key: "clubHouse", label: "Club House", responsive: "md" },
      ]
      : []),

    { key: "quantity", label: "Stock" },
    {
      key: "status",
      label: "Status",
      getClassName: (value: string) =>
        value === "Out of Stock"
          ? "text-red-600"
          : value === "Low in Stock"
            ? "text-yellow-600"
            : "text-green-600",
      responsive: "md",
    },
  ];

  useEffect(() => {
    const validActions = ["foods", "drinks"];
    const safeAction = (type || "").toLowerCase().trim();

    if (!validActions.includes(safeAction)) {
      navigate("/404");
      return;
    }
  }, [type, navigate]);

  const { openModal, closeModal } = useModal();

  const [itemCategories, setItemCategories] = useState<CategoryType[]>([]);
  const [items, setItems] = useState<MappedFoodDrink[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [autoFillData, setAutoFillData] = useState(initFormData);

  useEffect(() => {
    const loadItems = async () => {

       setLoading(true);

      const cache = await initAllStores();
      const getItems = async (): Promise<(Food | Drink)[]> => {
        return await cache.getAllBy(
          type as "foods" | "drinks", { terminal: terminalId }, "name"
        );
      }

      let itemsData = await getItems();

      if (!isEmptyObject(itemsData) && type) {
        await syncServices([type]);
        itemsData = await getItems();
      }

      const mappedItems: MappedFoodDrink[] = itemsData.map((item) => {
        let categoryName = "";
        let categoryId = "";

        if (type === "foods" && "foodCategory" in item) {
          categoryName = item.foodCategory.name;
          categoryId = item.foodCategory.id;
        } else if (type === "drinks" && "drinkCategory" in item) {
          categoryName = item.drinkCategory.name;
          categoryId = item.drinkCategory.id;
        }

        return {
          id: item.id,
          updatedAt: item?.updatedAt 
          ? formatDateTime(item?.updatedAt) : "",
          name: item.name,
          category: capitalizeFirst(categoryName),
          categoryId,
          price: item?.cashPrice?.toLocaleString(),
          openBar: item?.openBar?.toLocaleString(),
          gameHouse: item?.gameHouse?.toLocaleString(),
          vipLounge: item.vipLounge?.toLocaleString(),
          privateLounge: item?.privateLounge?.toLocaleString(),
          clubHouse: item?.clubHouse?.toLocaleString(),
          quantity: item?.quantity,
          description: item.description,
          status:
            item.quantity === 0
              ? "Out of Stock"
              : item.quantity < LOW_STOCK
                ? "Low in Stock"
                : "In Stock",
        };
      });

      const categories: CategoryType[] = [
        { id: "all", name: "All" },
        ...Array.from(
          itemsData.reduce((acc, item) => {
            if ("foodCategory" in item) {
              // For food items
              acc.set(item.foodCategory.id, item.foodCategory);
            } else if ("drinkCategory" in item) {
              // For drink items
              acc.set(item.drinkCategory.id, item.drinkCategory);
            }
            return acc;
          }, new Map<string, CategoryType>())
            .values()
        ),
      ];

      setItems(mappedItems);
      setItemCategories(categories);
      setLoading(false);
    };

    loadItems();
  }, []);

  const filteredItems = items.filter(
    (f) =>
      (activeCategory === "all" || f.categoryId === activeCategory) &&
      f.name.toLowerCase().includes(search.toLowerCase())
  );

  const showConfirmationModal = (data: any) => {
    const newItem = {
      item: {
        updatedAt: new Date(),
        createdAt: new Date(),
        id: data?.item?.id,
        name: capitalizeFirst(data?.item?.name ?? 0),
        price: parseFloat(data?.item?.price),
        openBar: parseFloat(data?.item?.openBar ?? 0),
        gameHouse: parseFloat(data?.item?.gameHouse ?? 0),
        vipLounge: parseFloat(data?.item?.vipLounge ?? 0),
        privateLounge: parseFloat(data?.item?.privateLounge ?? 0),
        clubHouse: parseFloat(data?.item?.clubHouse ?? 0),
        quantity: parseInt(data?.item?.stock ?? 0),
        description: data?.item?.description,
      },
      category: {
        ...data.category, name: capitalizeFirst(data?.category?.name ?? ""),
        updatedAt: new Date(),
        createdAt: new Date(),
      },
    };

    const missingFields: string[] = [];
    if (!newItem?.item?.name) missingFields.push("Food Name");
    if (!newItem?.item?.price) missingFields.push("Food Price");
    if (!newItem?.category?.name) missingFields.push("Food Category");
    if (!newItem?.item?.quantity && newItem?.item?.quantity !== 0) missingFields.push("Food Quantity");

    if (missingFields.length > 0) {
      return openModal(
        "Missing Fields",
        `Please fill in: ${missingFields.join(", ")}`,
        closeModal, false, "Close"
      );
    }

    if (newItem.category.isNew && !isEdit) {
      const isExist = itemCategories.find(
        (cat) => cat.name.toLocaleLowerCase() === newItem.category.name.toLocaleLowerCase()
      );
      if (isExist)
        return openModal(
          "Duplicate Category Detected",
          `A category named "${capitalizeFirst(newItem.category.name)}" already exists. 
           Please select it from the list instead of creating a new one.`,
          closeModal,
          false,
          "Close"
        );
    }

    const isFoodExist = items.find(
      (item) => item.name.toLocaleLowerCase() === newItem.item.name.toLocaleLowerCase()
        && item.id !== newItem.item.id
    );

    console.log(isFoodExist);

    if (isFoodExist) {
      return openModal(
        `Duplicate ${type && title[type]} Detected`,
        `A ${type?.slice(0, -1)} item named "${capitalizeFirst(newItem.item.name)}" already exists in the system. 
         Please choose a different name or edit the existing one instead.`,
        closeModal,
        false,
        "Close"
      );
    }

    const oldQty = parseInt(autoFillData.stock); // current stock
    const newQty = Number(newItem?.item?.quantity ?? 0); // user input
    const diff = newQty - oldQty;

    openModal(
      oldQty > 0 ? `Confirm ${type && title[type]} Update` : `Add New ${type && title[type]}`,
      diff === 0
        ? "Are you sure you want to update this item? The changes will overwrite the existing details."
        : oldQty > 0
          ? diff > 0
            ? `You're adding ${diff} ${newItem?.item?.name}(s) to the stock.
        Previous quantity: ${oldQty}, New quantity: ${newQty}. 
        Proceed?`
            : `You're removing ${Math.abs(diff)} ${newItem?.item?.name}(s) from the stock. Previous quantity: ${oldQty}, New quantity: ${newQty}. Proceed?`
          : `You're adding ${newQty} ${newItem?.item?.name}(s) as new stock.
      Proceed?`,
      () => {
        if (isEdit) updateItem(newItem);
        else createFood(newItem);
      }
    );


    // openModal(
    //   isEdit ? "Confirm Update" : `Add New ${type && title[type]} Item`,
    //   isEdit
    //     ? "Are you sure you want to update this item? The changes will overwrite the existing details."
    //     : `You're about to create "${capitalizeFirst(newItem?.item?.name)}" in the "${capitalizeFirst(newItem.category.name)}" category.
    //    Do you want to proceed?`,
    //   () => {
    //     if (isEdit) updateItem(newItem);
    //     else createFood(newItem);
    //   }
    // );

    // setFoods((prev) => [...prev, newFood]);
    // setIsModalOpen(false);
  };

  const createFood = async (itemData: any) => {
    const cache = await initAllStores();
    setIsModalOpen(false);
    setLoading(true);
    try {

      // Offline method not available now.
      if (!isOnline()) {
        const err = errorMessage(408);
        if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        return;
      }

      itemData.item.id = uuidv4();

      itemData.item.terminal = terminalId;
      itemData.category.terminal = terminalId;

      await sendRequest("POST", `/services/${type && type.slice(0, -1)}`, itemData);

      // ✅ Update foods list immediately
      setItems((prev) => [
        ...prev,
        {
          id: itemData.item.id,
          updatedAt: formatDateTime(new Date()),
          name: itemData.item.name,
          category: itemData.category.name,
          categoryId: itemData.category.id,
          // description: itemData.desrciption,
          price: itemData.item.price.toLocaleString(),
          openBar: (itemData?.item?.openBar ?? 0).toLocaleString(),
          gameHouse: (itemData?.item?.gameHouse ?? 0).toLocaleString(),
          vipLounge: (itemData?.item?.vipLounge ?? 0).toLocaleString(),
          privateLounge: (itemData?.item?.privateLounge ?? 0).toLocaleString(),
          clubHouse: (itemData?.item?.clubHouse ?? 0).toLocaleString(),
          quantity: itemData.item.quantity ?? 0,

          status:
            itemData.item.quantity === 0
              ? "Out of Stock"
              : itemData.item.quantity < LOW_STOCK
                ? "Low in Stock"
                : "In Stock",
        }
      ]);

      setItemCategories((prev) => {
        const exists = prev.some((cat) => cat.id === itemData.category.id);
        if (!exists && itemData.category.name.toLowerCase() !== "all") {
          return [...prev, {
            id: itemData.category.id,
            name: capitalizeFirst(itemData.category.name)
          }];
        }
        return prev;
      });

      if (!type) return;
      // const categoryKey = type === "foods" ? "foodCategory" : "drinkCategory";
      await cache.saveOne(type, {
        ...itemData.item, [`${categoryKey}Id`]: itemData.category.id,
        cashPrice: itemData.item.price,
        [categoryKey]: { ...itemData.category }
      });

      showToast("Item Added Successfully", { type: "success" });
    } catch (error: any) {
      const err = errorMessage(error.status);
      if (error.status === 409) {
        return openModal(
          "Duplicate Entry Detected",
          `An item or category with the name "${capitalizeFirst(itemData.item?.name || itemData.category?.name)}" already exists. 
           Please use a different name or select the existing one from the list.`,
          closeModal,
          false,
          "Close"
        );
      } else if (err) {
        openModal(err.title, err.msg, closeModal, false, "Close");
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const updateItem = async (itemData: any) => {
    const cache = await initAllStores();
    setLoading(true);
    if (!type) return;
    try {
      if (isOnline()) {
        await sendRequest("PUT", `/services/${type.slice(0, -1)}/${itemData.item.id}`, itemData.item);
      }

      await cache.updateManyBy(type, [itemData.item.id], {
        ...itemData.item,
        cashPrice: itemData.item.price,
        // [categoryKey]: { ...itemData.category }
      });

      // ✅ Update item in state
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemData.item.id
            ? {
              ...item,
              ...itemData.item,
              price: itemData.item.price.toLocaleString(),
              updatedAt: formatDateTime(new Date()),
              status:
                itemData.item.quantity === 0
                  ? "Out of Stock"
                  : itemData.item.quantity < LOW_STOCK
                    ? "Low in Stock"
                    : "In Stock",
              // cashPrice: itemData.item.price.toLocaleString(),
              // [categoryKey]: { ...itemData.category },
            }
            : item
        )
      );

      showToast("Item Updated Successfully", { type: "success" });
    } catch (error: any) {
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  async function deleteFood(id: string, categoryId: string, categoryName: string): Promise<void> {
    setLoading(true);
    if (!type) return;
    try {
      const cache = await initAllStores();
      if (isOnline()) {
        await sendRequest("DELETE", `/services/${type.slice(0, -1)}?id=${id}`);
      }

      await cache.deleteOne(type, id);

      setItems((prevItems) => prevItems.filter((item) => item.id !== id));

      const count = await cache.countBy(type, { [`${categoryKey}Id`]: categoryId });
      if (count < 1) setItemCategories((prevCats) => prevCats.filter((cat) => cat.id !== categoryId));
      showToast("Item Deleted Successfully", { type: "success" });

    } catch (error: any) {
      console.log(error.status);
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
      <InventoryHeader
        title={`${type && title[type]}s`}
        addLabel={`Add New ${type && title[type]}`}
        onAdd={
          () => {
            setAutoFillData(initFormData);
            setIsEdit(false);
            setIsModalOpen(true);
          }
        }
      />

      <InventoryStats
        total={items.length}
        lowStock={items.filter((f) => f.quantity < LOW_STOCK && f.quantity > 0).length}
        outOfStock={items.filter((f) => f.quantity === 0).length}
        onManageCategories={() => {
          //navigate("/inventory/foods/categories")
          openModal(
            "Coming Soon 🚀",
            "We’re still working on this feature. Stay tuned for the upcoming release.",
            closeModal, false, "Close"
          )
        }}
      />

      <CategoryFilter
        categories={itemCategories}
        activeCategory={activeCategory}
        onSelect={setActiveCategory}
      />

      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 items-center">

        {/* Add Food Modal */}
        <AddItemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          type={`${type && title[type]}` as "Food" | "Drink"}
          categories={itemCategories.filter((cat => cat.id !== "all"))}
          onSave={showConfirmationModal}
          isEdit={isEdit}
          autofillData={autoFillData}
        />

        <FullPageSpinner show={loading} />

        <input
          type="text"
          placeholder={`Search ${type} by name...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 w-full sm:w-1/2 focus:ring focus:ring-blue-300"
        />

        {/* <select className="border border-gray-300 rounded-lg px-3 py-2">
          <option>Sort by</option>
          <option>Price</option>
          <option>Stock</option>
          <option>Recently Added</option>
        </select> */}
      </div>

      {/* Foods Table */}
      <Table
        columns={columns}
        data={filteredItems}
        rowsPerPage={PAGINATION_PAGE_NUMBER}
        tableMenu={(row: MappedFoodDrink, closeMenu) => (
          <TableMenu
            row={row}
            closeMenu={closeMenu}
            options={[
              {
                label: `${title[type!]} Details`,
                color: "text-blue-500",
                onClick: (r: any) => {
                  openModal(
                    `${title[type!]} Details`,
                    `-------------------------------------------
                      Name: ${r.name}
                      Category: ${r.category}
                      Price: ₦${r.price}
                      Stock: ${r.quantity}
                      Status: ${r.quantity === 0
                      ? "Out of Stock"
                      : r.quantity < LOW_STOCK
                        ? "Low in Stock"
                        : "In Stock"}
                      -------------------------------------------`,
                    closeModal,
                    false,
                    "Close"
                  );
                },
              },
              {
                label: `Update ${type && title[type]}`,
                color: "text-blue-500",
                onClick: (data: any) => {
                  setAutoFillData({
                    id: data.id,
                    updatedAt: formatDateTime(data.updatedAt),
                    name: data.name,
                    category: data.category,
                    newCategory: data.categoryId,
                    price: data?.price?.replace(",", ""),
                    openBar: data?.openBar?.replace(",", ""),
                    gameHouse: data.gameHouse?.replace(",", ""),
                    vipLounge: data?.vipLounge?.replace(",", ""),
                    privateLounge: data?.privateLounge?.replace(",", ""),
                    clubHouse: data?.clubHouse?.replace(",", ""),
                    stock: data.quantity,
                    description: data.description,
                    status: undefined,
                  });
                  setIsEdit(true);
                  setIsModalOpen(true);
                }
              },
              {
                label: `Delete ${type && title[type]}`,
                color: "text-red-500",
                onClick: (r: any) => {
                  return openModal(
                    "Confirm Delete",
                    `Are you sure you want to delete "${capitalizeFirst(r.name)}"? This action cannot be undone.`,
                    () => {
                      closeModal();
                      deleteFood(r.id, r.categoryId, r.category);
                    },
                    true, "Delete", "Cancel", "red"
                  );
                },
              },
            ] as MenuOption<MappedFoodDrink>[]}
          />
        )}
      />

    </div>
  );
};

export default FoodDrinkInventory;